import { api } from "@/lib/api";

export function pushSupported() {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function ensureServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration("/");
    if (existing) return existing;
    return await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  } catch (e) {
    return null;
  }
}

function detectPlatform() {
  const ua = navigator.userAgent || "";
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad|ipod/i.test(ua)) return "iOS";
  return "Web";
}

// Requests permission (must be called from a user gesture), subscribes and saves to backend.
export async function enablePush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    await api.post("/push/preference", { permission_status: permission }).catch(() => {});
    return { ok: false, reason: permission }; // "denied" | "default"
  }
  const reg = await ensureServiceWorker();
  if (!reg) return { ok: false, reason: "unsupported" };
  await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public-key");
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.publicKey),
    });
  }
  await api.post("/push/subscribe", {
    subscription: sub.toJSON(),
    permission_status: "granted",
    user_agent: navigator.userAgent || "",
    platform: detectPlatform(),
  });
  return { ok: true };
}

export async function disablePush() {
  let endpoint = null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/");
    const sub = reg && (await reg.pushManager.getSubscription());
    if (sub) {
      endpoint = sub.endpoint;
      await sub.unsubscribe().catch(() => {});
    }
  } catch (e) { /* noop */ }
  await api.post("/push/unsubscribe", { endpoint });
  return { ok: true };
}

export async function dismissPrompt() {
  await api.post("/push/preference", { dismissed_prompt: true }).catch(() => {});
}
